<template>
  <nav
    class="navbar sticky top-0 z-50 flex items-center gap-[128px] border-b bg-white px-6"
  >
    <div class="flex grow select-none flex-row items-center gap-4">
      <img
        :src="logoSrc"
        alt="Logo"
        class="max-h-14"
        @mouseover="hover = true"
        @mouseleave="hover = false"
      >

      <MaterialIcon
        v-if="user?.email"
        v-ripple="{
          pt: {
            root: {
              class: 'bg-aubergine-200',
            },
          },
        }"
        icon="apps"
        class="p-ripple rounded-full p-4"
        color="aubergine-dark-500"
        @click="menuVisible = !menuVisible"
      />

      <DevVersionBadge />
    </div>

    <Drawer
      v-model:visible="menuVisible"
      :dismissable="false"
      class="mt-[114px] translate-y-0 bg-softlight-500 transition-[opacity,transform]"
      position="full"
      :show-close-icon="false"
      :modal="false"
      :pt="{
        transition: {
          enterFromClass: 'translate-y-10 opacity-0',
          leaveToClass: 'translate-y-10 opacity-0',
          enterActiveClass: 'transition-[opacity,transform]',
          leaveActiveClass: 'transition-[opacity,transform]',
        },
      }"
    >
      <div
        class="flex h-[calc(100%-114px)] flex-col items-center justify-center gap-8 text-aubergine-dark-500"
      >
        <div>
          <h3 class="mb-1">
            {{ $t('header.tasks') }}
          </h3>
          <div class="grid grid-cols-5 gap-4">
            <HeaderMenuItem
              v-if="canAccessPage(PageName.CHECK_DELIVERY)"
              link="/controle-levering"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="task"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.checkDelivery') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="canAccessPage(PageName.PRINT)"
              link="/printen"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="print"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.labelPrinting') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="canAccessPage(PageName.ORDERS)"
              link="/bestellingen"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="shopping_basket"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.orderRequest') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="canAccessPage(PageName.LOAD_FILES)"
              link="/bestanden-inladen"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="upload_file"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.loadFiles') }}
            </HeaderMenuItem>

            <HeaderMenuItem
              v-if="canAccessPage(PageName.BACKORDERS)"
              link="/nazendingen"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="package_2"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.backorders') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="canAccessPage(PageName.PARALLEL_ORDER)"
              link="/parallel-bestellen"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="local_mall"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.parallelOrder') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="canAccessPage(PageName.PHARMACIST_INSPECTION)"
              link="/apothekers-controle"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="mystery"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.pharmacistsInspection') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="canAccessPage(PageName.PRESCRIPTION_PROCESSING)"
              link="/receptverwerking-paginated"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="prescriptions"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.prescriptionProcessing') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              link="/wijzigingen"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="autorenew"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              <span class="flex w-full items-center justify-center text-center">
                {{ $t('header.mutationRequests') }}
              </span>
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="canAccessPage(PageName.PLANNING_ADJUSTMENTS)"
              link="/planning-aanpassingen"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="edit_calendar"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              <span class="flex w-full items-center justify-center text-center">
                {{ $t('header.planningAdjustments') }}
              </span>
            </HeaderMenuItem>
            <HeaderMenuItem
              link="/intake-gesprek"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="phone_in_talk"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              <span class="flex w-full items-center justify-center text-center">
                {{ $t('header.intakes') }}
              </span>
            </HeaderMenuItem>
            <HeaderMenuItem
              link="/partner-inschrijving"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="diversity_3"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              <span class="flex w-full items-center justify-center text-center">
                {{ $t('header.partnerSubscription') }}
              </span>
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="canAccessPage(PageName.CONTROL_PROFILE)"
              link="/controle-profiel"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="frame_person"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.convertProfile') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              link="/planned-deliveries"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="delivery_truck_bolt"
                class="mx-auto block"
                color="aubergine-dark-500"
              />
              <span>{{ $t('header.plannedDeliveries') }}</span>
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="$hasRole('Admin')"
              link="/planned-deliveries/anomalies"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="crisis_alert"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              <span class="flex w-full items-center justify-center text-center">
                {{ $t('header.planningAnomalies') }}
              </span>
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="canAccessPage(PageName.DISPENSE_GUIDANCE)"
              link="/dispense-guidance"
              @click="closeMenu"
            >
              <KernelIcon
                name="medical-information-rounded"
                class="mx-auto block"
              />
              <span class="flex w-full items-center justify-center text-center">
                {{ $t('dispenseGuidance.title') }}
              </span>
            </HeaderMenuItem>
          </div>
        </div>
        <div>
          <h3 class="mb-1">
            {{ $t('header.pages') }}
          </h3>
          <div class="grid grid-cols-5 gap-4">
            <HeaderMenuItem
              link="/klantprofiel"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="account_circle"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.clientProfile') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              link="/"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="search"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.deliveries') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              link="/leveringen-overzicht"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="search_activity"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.parcelOverview') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="canAccessPage(PageName.GDS_PLANNING)"
              link="/gds-planning"
              @click="closeMenu"
            >
              <img
                class="mx-auto block"
                src="/images/gds-icon.svg"
                alt="GDS icon"
                color="aubergine-dark-500"
              >
              {{ $t('header.gdsPlanning') }}
            </HeaderMenuItem>
            <HeaderMenuItem
              v-if="canAccessPage(PageName.INVOICES)"
              link="/facturen"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="euro_symbol"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.invoices') }}
            </HeaderMenuItem>
          </div>
        </div>
        <div v-if="canAccessPage(PageName.ACCOUNTS)">
          <h3 class="mb-1">
            {{ $t('header.settings') }}
          </h3>
          <div class="grid grid-cols-5 gap-4">
            <HeaderMenuItem
              link="/accounts"
              @click="closeMenu"
            >
              <MaterialIcon
                icon="manage_accounts"
                color="aubergine-dark-500"
                class="mx-auto block"
              />
              {{ $t('header.accounts') }}
            </HeaderMenuItem>
          </div>
        </div>
      </div>
    </Drawer>

    <div class="flex items-center gap-2">
      <UziLogin />
      <MaisNavbarUser />
    </div>

    <OverrideRolesDialog />
  </nav>
</template>

<script lang="ts" setup>
import { PageName } from '~/config'
import HeaderMenuItem from './HeaderMenuItem.vue'
import MaterialIcon from './MaterialIcon.vue'

const { user } = useUser()
const menuVisible = ref(false)
const hover = ref(false)
const logoSrc = computed((): string =>
  hover.value ? '/images/logo-hover.svg' : '/images/logo.svg',
)

const { canAccessPage } = useRights()

function closeMenu() {
  menuVisible.value = false
}
</script>

<style lang="css">
.material-design-icon__svg {
  margin: 0 auto;
}
</style>
